/**
 * lensGroups (#2373, #2407, #2408) — crop-level grouping for the Ernteprognose
 * lens ("lens v3").
 *
 * v2 (#2373) grouped by exact (Kultur, Sorte), which shatters on real variety
 * data: crops with mixed tagging (a WF Ackerbohnen register carries bare rows
 * AND per-variety rows across years) fragment into a bare group + per-variety
 * siblings, none of which has year-over-year continuity — the −1/−2/−3-Jahr
 * overlays starve and a bare crop row reads as the crop total when it is only
 * the untagged remainder. v3 groups by KULTUR: one `CropLensGroup` per crop
 * aggregating ALL its cycles (tagged + untagged, every crop_year), with variety
 * exposed as an in-group refinement (`varieties` + the explicit "ohne Sorte"
 * remainder) for the #2408-B refinement UI. Pure + unit-tested (no React).
 *
 * The lens id is a GROUP key; ``?cc=`` stays a crop_cycle id (the per-field
 * table drills on it). The selected group is DERIVED from ``?cc=`` (the group
 * that contains it), so a ``?cc=`` deep link still resolves to its Kultur.
 */
import { kulturGroupKey } from '../deckungsbeitrag/deckungsbeitragRollup';
import type { OutputCycleInfo } from '../../../hooks/useErnteprognose';

/** Blank-crop display fallback — the lens NEVER renders a crop_cycle UUID. */
export const UNBENANNTE_KULTUR_LABEL = 'Unbenannt';

/**
 * Untagged-remainder refinement label (#2408 fix 3): a blank VARIETY inside a
 * crop group. Distinct from ``UNBENANNTE_KULTUR_LABEL`` (a blank CROP) — the
 * two blanks mean different things and must never share a label.
 */
export const OHNE_SORTE_LABEL = 'ohne Sorte';

/**
 * ``?s=`` sentinel for the ohne-Sorte remainder (#2408-B). The refinement URL
 * param carries the RAW variety name, so the untagged remainder — whose
 * "variety" is null — needs an explicit sentinel: ``?s=~``. Consequence: ``~``
 * can never address a variety literally named "~" (the sentinel takes
 * precedence in ``resolveRefinement``; such a variety stays selectable in the
 * lens, it is just not deep-linkable — the unknown-``?s=`` degrade keeps that
 * safe).
 */
export const OHNE_SORTE_SENTINEL = '~';

/** Data-quality hint under a crop with an untagged remainder (#2322-class cause). */
export function ohneSorteHintText(n: number): string {
  return `Für ${n} ${n === 1 ? 'Schlag' : 'Schläge'} ist keine Sorte hinterlegt.`;
}

// The #2373/#2407 non-harvest name blocklist (``NON_HARVEST_CROP_KEYS`` /
// ``normCrop`` / ``isNonHarvestCrop``) is RETIRED (#2419). Data-presence hides
// Brache/Wege/Blühstreifen without naming them: those strips carry no picks and
// no current forecast, so ``isZeroDataGroup`` collapses them out of the lens.
// The strips stay REAL members of the unfiltered ``groups`` (a deep link into
// one renders an honest empty banner) — only ``visibleGroups`` drops them.

/** Kultur · Sorte display label — no year suffix; blank crop → "Unbenannt". */
export function kulturSorteLabel(crop: string | null, variety: string | null): string {
  const cropDisplay = (crop ?? '').trim() || UNBENANNTE_KULTUR_LABEL;
  const v = (variety ?? '').trim();
  return v ? `${cropDisplay} · ${v}` : cropDisplay;
}

/**
 * Numeric ``crop_year`` of a cycle — the HARVEST SEASON (the calendar year the
 * crop is harvested in), NOT the Wirtschaftsjahr start year — or null when the
 * year is absent or non-numeric. Null-cropYear cycles stay group MEMBERS but are
 * excluded from current-season / prior-offset / future detection everywhere —
 * ``Number(null)`` is 0, which would otherwise fake a huge prior offset.
 */
export function cropYearNumOf(cycle: OutputCycleInfo): number | null {
  if (cycle.cropYear == null) return null;
  const n = Number(cycle.cropYear);
  return Number.isFinite(n) ? n : null;
}

/**
 * An in-group variety refinement (#2408): the members of ONE variety — or of
 * the untagged remainder (``variety: null``, labelled "ohne Sorte") — inside a
 * crop-level group. Refinement selection UI ships in #2408-B; this model layer
 * only exposes the data.
 */
export interface VarietyRefinement {
  /** Namespaced id — see ``varietyRefinementId``; can never collide with a group key. */
  id: string;
  /** Raw variety string; null = the untagged remainder. */
  variety: string | null;
  label: string;
  members: OutputCycleInfo[];
  currentWjCount: number;
}

/** One crop-level lens entry: a Kultur group over ALL of its field-cycles. */
export interface CropLensGroup {
  /** Stable crop-level key = ``kulturGroupKey(rawCrop, null)`` (keyed on RAW crop). */
  groupKey: string;
  label: string;
  crop: string | null;
  /** Member crop_cycle ids across ALL crop_years, tagged + untagged (priors feed the overlays). */
  memberIds: string[];
  members: OutputCycleInfo[];
  /**
   * Members that fall in the current SEASON (``crop_year === currentSeason``) —
   * the lens count + table rows. Name kept for continuity across #2601/#2610;
   * the value is a season count, not a Wirtschaftsjahr one.
   */
  currentWjCount: number;
  /** Tagged-variety refinements (label-sorted, de) — only where variety data exists. */
  varieties: VarietyRefinement[];
  /**
   * The untagged remainder — non-null ONLY when tagged and untagged cycles
   * coexist (an all-untagged crop needs no refinement axis; an all-tagged crop
   * has no remainder). Never a bare sibling group.
   */
  ohneSorte: VarietyRefinement | null;
}

/**
 * Namespaced refinement id: ``groupKey + NUL + 'sorte' + NUL + variety``. A
 * crop-level ``kulturGroupKey(crop, null)`` contains exactly one NUL, so a
 * refinement id (≥3 NULs) can never collide with any group key — required
 * because #2408-B routes refinement selection through ids next to group keys.
 */
export function varietyRefinementId(groupKey: string, variety: string | null): string {
  return `${groupKey}\u0000sorte\u0000${variety ?? ''}`;
}

const countCurrentWj = (members: OutputCycleInfo[], currentWj: number): number =>
  members.filter((m) => cropYearNumOf(m) === currentWj).length;

const toRefinement = (
  groupKey: string,
  variety: string | null,
  label: string,
  members: OutputCycleInfo[],
  currentWj: number,
): VarietyRefinement => ({
  id: varietyRefinementId(groupKey, variety),
  variety,
  label,
  members,
  currentWjCount: countCurrentWj(members, currentWj),
});

/**
 * Group crop_cycles into one entry per Kultur (lens v3, #2408), keying on the
 * RAW crop (so a blank crop can never merge with a real crop literally named
 * "Unbenannt"). Members span every crop_year AND every variety — tagged and
 * untagged cycles pool into the same group, so overlays/aggregation survive
 * discontinuous variety tagging. Variety data is exposed as refinements; the
 * untagged remainder becomes the explicit "ohne Sorte" refinement only where
 * tagged cycles coexist. Sorted by label (de). #2419: no name blocklist —
 * ``groups`` is UNFILTERED (deep-link invariant); ``isZeroDataGroup`` is what
 * drops the no-data strips from ``visibleGroups``.
 */
export function buildCropLensGroups(cycles: OutputCycleInfo[], currentWj: number): CropLensGroup[] {
  const byCrop = new Map<string, OutputCycleInfo[]>();
  for (const c of cycles) {
    const key = kulturGroupKey(c.crop ?? '', null);
    const bucket = byCrop.get(key) ?? [];
    bucket.push(c);
    byCrop.set(key, bucket);
  }
  const out = [...byCrop.entries()].map(([key, members]): CropLensGroup => {
    const byVariety = new Map<string, OutputCycleInfo[]>();
    const untagged: OutputCycleInfo[] = [];
    for (const m of members) {
      if ((m.variety ?? '').trim() === '') {
        untagged.push(m);
      } else {
        const bucket = byVariety.get(m.variety as string) ?? [];
        bucket.push(m);
        byVariety.set(m.variety as string, bucket);
      }
    }
    const varieties = [...byVariety.entries()]
      .map(([variety, vMembers]) => toRefinement(key, variety, variety.trim(), vMembers, currentWj))
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
    const ohneSorte =
      varieties.length > 0 && untagged.length > 0
        ? toRefinement(key, null, OHNE_SORTE_LABEL, untagged, currentWj)
        : null;
    return {
      groupKey: key,
      label: kulturSorteLabel(members[0].crop, null),
      crop: members[0].crop,
      memberIds: members.map((m) => m.cropCycleId),
      members,
      currentWjCount: countCurrentWj(members, currentWj),
      varieties,
      ohneSorte,
    };
  });
  return out.sort((a, b) => a.label.localeCompare(b.label, 'de'));
}

/**
 * The group key to select: the group CONTAINING the ``?cc=`` cycle (deep-link),
 * else the first fallback group, else null. The lens selects a group but ``?cc=``
 * stays a crop_cycle id, so selection is derived from it rather than a separate
 * param. ``fallbackGroups`` (#2407 Gap 3) lets the page default onto the first
 * VISIBLE group while a deep link still resolves over ALL groups — a ``?cc=``
 * into a collapsed zero-data group must keep rendering (never 404).
 */
export function resolveSelectedGroup(
  ccParam: string | null,
  groups: CropLensGroup[],
  fallbackGroups: CropLensGroup[] = groups,
): string | null {
  if (ccParam) {
    const found = groups.find((g) => g.memberIds.includes(ccParam));
    if (found) return found.groupKey;
  }
  return fallbackGroups[0]?.groupKey ?? groups[0]?.groupKey ?? null;
}

/**
 * #2419/#2435 — does ANY of these member cycles carry something the chart can
 * DRAW? The ONE shared drawable predicate for BOTH the level-1 crop filter
 * (``isZeroDataGroup``) and the level-2 variety filter
 * (``visibleRefinementSubItems``), so the two axes can never diverge on what
 * "has data" means. A member cycle ``C`` (crop_year ``Y``, offset
 * ``k = currentSeason − Y``) is DRAWABLE iff:
 *   - ``k === 0`` AND ``C`` has picks (``pick``) → draws as the current
 *     season's Ist, OR
 *   - ``k === 0`` AND ``C`` has a current-season forecast/plan
 *     (``currentForecast``) → a farmer forecasting at season start stays
 *     visible before the first pick, OR
 *   - ``k ∈ {1,2,3}`` AND ``C`` has picks (``pick``) →
 *     ``buildPriorYearSeries`` shifts it into the −k overlay.
 * Members with ``k<0`` (future) or ``k>3`` (too old) are never drawable on
 * their own.
 *
 * #2601/#2610 — this used to take TWO pick sets (a current-WJ window and a
 * four-WJ span). Both are gone: seasons are identified by ``crop_year``, and a
 * season's Ist is its members' FULL pick span, so the only pick question left is
 * "does this cycle have harvest at all". Keeping a wall-clock window here is
 * what hid WF Spargel (harvest Mar–Jun, invisible after the July rollover) and
 * bisected Erdbeeren in April. ``offset`` — not a date — decides which season a
 * cycle draws in, and ``shiftYears`` moves prior seasons onto the axis whole.
 *
 * #2599 — what ``pick`` MEANS is the other half of this predicate's honesty,
 * and it was wrong. It was "this cycle has a pick ROW"; the chart sums ``qty``.
 * WF Äpfel/Trauben carry ``qty = 0.0`` on every pick, so they satisfied the
 * predicate, entered the lens, and drew a flat zero. ``pick`` is now
 * "Σ``qty`` > 0" (``useErnteprognose``'s ``facetItemsToDrawableSet``) — the
 * same quantity ``buildPriorYearSeries`` cumulates — so this predicate really
 * does match what the chart draws, rather than proxying it. Callers must pass
 * the drawable set; passing a row-existence set silently re-opens the gap.
 * ``currentForecast`` already carries the equivalent guard (codex-#2419-P2:
 * a forecast counts only with a non-null ``value``).
 */
export function hasDrawableMember(
  members: OutputCycleInfo[],
  currentSeason: number,
  pick: ReadonlySet<string>,
  currentForecast: ReadonlySet<string>,
): boolean {
  return members.some((m) => {
    const y = cropYearNumOf(m);
    if (y == null) return false;
    const offset = currentSeason - y;
    if (offset === 0) {
      return pick.has(m.cropCycleId) || currentForecast.has(m.cropCycleId);
    }
    if (offset >= 1 && offset <= 3) {
      return pick.has(m.cropCycleId);
    }
    return false;
  });
}

/**
 * #2419 — true when a crop group has nothing the chart can DRAW. Replaces the
 * #2407/#2408 membership proxy (a crop was shown for merely HAVING a current or
 * prior member) with a real data-presence signal (``hasDrawableMember`` over the
 * group's members). A group is visible iff ANY member is drawable — so a SHOWN
 * crop always has something drawable and the lens never disagrees with the
 * chart. Display-only — callers filter what the LENS lists, never ``groups``
 * itself (deep-link resolution runs over all groups). (Switching the overlay
 * engine to pick-date offsets is out of scope.)
 */
export function isZeroDataGroup(
  group: CropLensGroup,
  currentSeason: number,
  pick: ReadonlySet<string>,
  currentForecast: ReadonlySet<string>,
): boolean {
  return !hasDrawableMember(group.members, currentSeason, pick, currentForecast);
}

/**
 * The crop_cycle a member-subset selection should stamp into ``?cc=`` —
 * shared by lens-group clicks, the default-``?cc=`` stamp, the Planung
 * banner's "switch to current season" and the #2408-B refinement selection
 * (which stamps a member of the REFINED subset). Deterministic rank:
 * current-WJ member → most-recent past member → earliest future member →
 * null-cropYear members last; ties break on the id.
 * (v2 fell back to unsorted ``members[0]`` — fetch-order, not deterministic.)
 */
export function firstCycleOfMembers(members: OutputCycleInfo[], currentWj: number): string | null {
  const rankOf = (m: OutputCycleInfo): [number, number] => {
    const y = cropYearNumOf(m);
    if (y == null) return [3, 0];
    if (y === currentWj) return [0, 0];
    if (y < currentWj) return [1, -y]; // most-recent past first
    return [2, y]; // earliest future first
  };
  const ranked = [...members].sort((a, b) => {
    const ra = rankOf(a);
    const rb = rankOf(b);
    if (ra[0] !== rb[0]) return ra[0] - rb[0];
    if (ra[1] !== rb[1]) return ra[1] - rb[1];
    return a.cropCycleId < b.cropCycleId ? -1 : a.cropCycleId > b.cropCycleId ? 1 : 0;
  });
  return ranked[0]?.cropCycleId ?? null;
}

/** ``firstCycleOfMembers`` over a whole crop group (the crop-total subset). */
export function firstCycleOfGroup(group: CropLensGroup, currentWj: number): string | null {
  return firstCycleOfMembers(group.members, currentWj);
}

/**
 * Resolve ``?s=`` to the selected in-group refinement (#2408-B / #2454). The
 * param carries the RAW variety name (url-encoded) or the ``~`` sentinel for the
 * ohne-Sorte remainder. Precedence on inconsistent URLs: ``?s=`` SOLELY
 * determines the refinement while ``?cc=`` determines the group — so resolution
 * runs inside the ``?cc=``-derived group only.
 *
 * #2454 deep-link invariant #1 — resolution runs against the UNFILTERED axis
 * (``refinementSubItems``), NOT the data-presence-filtered display axis
 * (``visibleRefinementSubItems``): a ``?s=`` into a variety that #2435 hides for
 * lack of data STILL resolves and renders its (honest, possibly empty) view,
 * exactly as ``resolveSelectedGroup`` resolves a ``?cc=`` over the unfiltered
 * ``groups`` at level 1. Only the LENS DISPLAY is filtered — deep-link selection
 * is not. Still degrading to null = crop total (no dead-end): an unknown/foreign
 * variety, the sentinel without a remainder, the sole variety of a
 * single-refinement crop (below the ≥2 gate that lives in ``refinementSubItems``,
 * so its axis — and thus resolution — is empty), or a missing group. ``?cc=``
 * alone NEVER auto-refines.
 */
export function resolveRefinement(sParam: string | null, group: CropLensGroup | null): VarietyRefinement | null {
  if (!sParam || !group) return null;
  const axis = refinementSubItems(group);
  if (sParam === OHNE_SORTE_SENTINEL) return axis.find((r) => r.variety == null) ?? null;
  return axis.find((r) => r.variety != null && r.variety === sParam) ?? null;
}

/**
 * The refinement axis the lens renders under a crop (#2408-B): varieties
 * (de-sorted) + the ohne-Sorte remainder LAST — but only when there are ≥2
 * distinct refinements (≥2 varieties, or 1 variety + remainder). A single
 * refinement equals the crop total (meaningless axis) and a fully-untagged
 * crop has no axis at all → empty, the lens shows no sub-items. Unfiltered
 * building block (kept for the pure threshold rule + its unit tests); the LENS
 * renders ``visibleRefinementSubItems`` (#2435), which applies the SAME
 * threshold to the data-present subset.
 */
export function refinementSubItems(group: CropLensGroup): VarietyRefinement[] {
  const all = group.ohneSorte ? [...group.varieties, group.ohneSorte] : group.varieties;
  return all.length >= 2 ? all : [];
}

/**
 * #2435 — the DATA-PRESENCE-filtered refinement axis: extends #2419's level-1
 * crop filter down to the level-2 variety sub-lenses. A variety (or the
 * ohne-Sorte remainder) is kept only when it has a drawable member
 * (``hasDrawableMember`` — the SAME signal ``isZeroDataGroup`` uses), THEN the
 * existing ``≥2 distinct refinements`` threshold applies to the FILTERED set.
 * So a variety with no picks and no current-season forecast
 * (e.g. a register-only ``Backlim`` on a Spargel crop) drops out, and a crop
 * left with a single data-carrying refinement collapses its axis (== crop
 * total, meaningless as a sub-lens). Filters what the lens LISTS, never
 * ``group.varieties`` / ``group.ohneSorte`` themselves (deep-link invariant,
 * mirroring #2419). DISPLAY-only: ``resolveRefinement`` deliberately does NOT
 * consume this axis — a ``?s=`` deep-link resolves over the UNFILTERED
 * ``refinementSubItems`` (#2454 invariant #1) so a now-hidden variety still
 * renders its view. This axis feeds ``lensItems`` (what the lens lists) and the
 * click handler (you can only click what is rendered).
 */
export function visibleRefinementSubItems(
  group: CropLensGroup,
  currentSeason: number,
  pick: ReadonlySet<string>,
  currentForecast: ReadonlySet<string>,
): VarietyRefinement[] {
  const hasData = (r: VarietyRefinement): boolean =>
    hasDrawableMember(r.members, currentSeason, pick, currentForecast);
  const varieties = group.varieties.filter(hasData);
  const ohneSorte = group.ohneSorte && hasData(group.ohneSorte) ? group.ohneSorte : null;
  const all = ohneSorte ? [...varieties, ohneSorte] : varieties;
  return all.length >= 2 ? all : [];
}
